import type { Persona } from "@shopify-harness/sdk";
import { createFileRoute, type ErrorComponentProps, Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { FilterSearch, FilterSelect } from "@/components/data-table/filter-controls";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState, ErrorState } from "@/components/states/state-views";
import { StatusBadge } from "@/components/status/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonaMatrix } from "@/features/personas/persona-matrix";
import { personaStatusPresentation } from "@/features/personas/persona-presentation";
import { personasQueryOptions } from "@/features/personas/queries";
import { personasSearchSchema } from "@/features/personas/schemas";
import { formatInteger, formatPercent, formatRelative } from "@/lib/format";

export const Route = createFileRoute("/personas/")({
  validateSearch: personasSearchSchema,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(personasQueryOptions(context.api.personas)),
  pendingComponent: PersonasLoading,
  errorComponent: PersonasError,
  component: PersonasPage
});

/**
 * AI がストアデータから生成したペルソナのギャラリー。
 * 生成・更新は Claude Code（MCP の upsert_persona）が行い、この画面は閲覧に徹する。
 */
function PersonasPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const data = Route.useLoaderData();

  const update = (patch: Partial<typeof search>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const filtered = useMemo(() => {
    const keyword = search.q.toLowerCase();
    return data.personas.filter((persona) => {
      if (search.status !== "all" && persona.status !== search.status) return false;
      if (!keyword) return true;
      return `${persona.name} ${persona.tagline} ${persona.segment ?? ""}`
        .toLowerCase()
        .includes(keyword);
    });
  }, [data.personas, search.q, search.status]);

  const isFiltered = search.q !== "" || search.status !== "all";

  // 座標（該当顧客数 × 売上構成比）が揃っているものだけマトリクスに配置する
  const placeable = filtered.filter(
    (persona) => persona.contactCount !== null && persona.revenueShare !== null
  );
  const unplaceable = filtered.filter(
    (persona) => persona.contactCount === null || persona.revenueShare === null
  );

  return (
    <>
      <PageHeader
        kind="data"
        title="ペルソナ"
        description="AIが顧客・注文データから生成した代表顧客像。投入・更新は Claude Code（MCP）から行う"
      />

      <PageBody density="dense">
        <Panel
          title="ペルソナマトリクス"
          count={formatInteger(filtered.length)}
          description="横軸=売上構成比・縦軸=該当顧客数。カードを開くと根拠データつきの詳細"
          flush
        >
          <div className="flex flex-wrap items-end gap-3 border-b border-line px-4 py-3">
            <FilterSearch
              label="名前・キャッチコピー・セグメント"
              value={search.q}
              onChange={(value) => update({ q: value })}
              placeholder="ペルソナを検索"
            />
            <FilterSelect
              label="ステータス"
              value={search.status}
              onChange={(value) => update({ status: value })}
              options={[
                { value: "all", label: "すべて" },
                { value: "draft", label: "ドラフト" },
                { value: "active", label: "有効" },
                { value: "archived", label: "アーカイブ" }
              ]}
            />
          </div>

          {filtered.length > 0 ? (
            <div className="grid gap-4 p-4">
              {placeable.length > 0 ? (
                <PersonaMatrix personas={placeable} />
              ) : (
                <p className="rounded-md border border-dashed border-line px-4 py-6 text-center text-sm text-fg-subtle">
                  座標に配置できるペルソナがありません。upsert_persona で contact_count と
                  revenue_share を投入すると、ここにマトリクスが表示されます。
                </p>
              )}

              {unplaceable.length > 0 ? (
                <section>
                  <h3 className="eyebrow mb-2">座標未算出（該当顧客数・売上構成比が未設定）</h3>
                  <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {unplaceable.map((persona) => (
                      <li key={persona.id}>
                        <PersonaCard persona={persona} />
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          ) : isFiltered ? (
            <EmptyState
              title="条件に一致するペルソナがありません"
              description="検索語を短くするか、ステータスの絞り込みを解除してください。"
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => update({ q: "", status: "all" })}
                >
                  絞り込みを解除
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="ペルソナがまだ生成されていません"
              description="Claude Code に「顧客データからペルソナを作って」と依頼すると、MCP ツール upsert_persona 経由でここに投入されます。"
            />
          )}
        </Panel>
      </PageBody>
    </>
  );
}

function PersonaCard({ persona }: { persona: Persona }) {
  const presentation = personaStatusPresentation(persona.status);

  return (
    <Link
      to="/personas/$personaId"
      params={{ personaId: persona.id }}
      className="flex h-full flex-col gap-2 rounded-lg border border-line bg-surface p-4 transition-colors hover:border-line-strong hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-fg">{persona.name}</p>
        <StatusBadge tone={presentation.tone}>{presentation.label}</StatusBadge>
      </div>

      <p className="line-clamp-2 text-xs text-fg-muted">{persona.tagline}</p>

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-2xs text-fg-subtle">
        {persona.segment ? <Badge variant="outline">{persona.segment}</Badge> : null}
        {persona.contactCount !== null ? (
          <span className="num">該当 {formatInteger(persona.contactCount)}人</span>
        ) : null}
        {persona.revenueShare !== null ? (
          <span className="num">売上比 {formatPercent(persona.revenueShare)}</span>
        ) : null}
        <span className="num ml-auto">{formatRelative(persona.updatedAt)}に更新</span>
      </div>
    </Link>
  );
}

function PersonasLoading() {
  return (
    <>
      <PageHeader kind="data" title="ペルソナ" />
      <PageBody density="dense">
        <Panel title="ペルソナ一覧" flush>
          <div
            role="status"
            aria-label="読み込み中"
            className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3"
          >
            {Array.from({ length: 6 }, (_, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: 静的なプレースホルダカード
                key={index}
                className="grid gap-2.5 rounded-lg border border-line p-4"
              >
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </Panel>
      </PageBody>
    </>
  );
}

function PersonasError({ error, reset }: ErrorComponentProps) {
  return (
    <>
      <PageHeader kind="data" title="ペルソナ" />
      <PageBody density="dense">
        <Panel title="ペルソナ一覧" flush>
          <ErrorState
            title="ペルソナを読み込めませんでした"
            message={error.message}
            onRetry={reset}
          />
        </Panel>
      </PageBody>
    </>
  );
}
